// 办赛指南(/guide)与项目落地页(/sports)的正文。
// 写法遵循 GEO:每篇开头先给「一句话回答」,再分小节给定义、步骤、表格和 FAQ,
// 方便搜索引擎摘要和 AI 问答直接引用。内容口径以灵缇百科、资讯里已发布的规则说明为准,改动请同步核对。

export type Block = {
  h2: string
  p?: string[]
  ul?: string[]
  ol?: string[]
  table?: { head: string[]; rows: string[][] }
}

export type QA = { q: string; a: string }

export type Guide = {
  slug: string
  title: string
  h1: string
  description: string
  keywords: string
  tldr: string
  updated: string
  blocks: Block[]
  faq: QA[]
  // 操作步骤(有则输出 HowTo 结构化数据)
  howto?: { name: string; steps: string[] }
  related: [string, string][]
}

export type Sport = {
  slug: string
  name: string
  title: string
  h1: string
  description: string
  keywords: string
  tldr: string
  formats: { name: string; desc: string }[]
  blocks: Block[]
  faq: QA[]
  related: [string, string][]
}

// 截至 2026-10 的生产库统计,用作信任信号;更新时重跑:
// Match.count / Team.count / User.count / Match.minimum(:created_at)
export const STATS = {
  asOf: '2026 年 10 月',
  since: '2022 年 9 月',
  matches: '15,000+',
  teams: '27 万+',
  users: '9.3 万+',
}

const UPDATED = '2026-10-22'

export const GUIDES: Guide[] = [
  {
    slug: 'guandan-rules',
    title: '掼蛋规则入门:怎么打、牌型大小、炸弹排序、进贡还贡、升级与过A · 灵缇办赛指南',
    h1: '掼蛋规则入门:一文看懂怎么打',
    description:
      '掼蛋用两副牌 108 张、四人两两结对,每人 27 张,比谁先出完牌来升级,从 2 打到 A。本文按《竞技掼蛋竞赛规则》讲清牌型、牌点与炸弹大小、红心级牌(逢人配)、进贡还贡与抗贡、升级与过 A、报牌。',
    keywords: '掼蛋规则,掼蛋怎么打,掼蛋牌型,掼蛋牌型大小,掼蛋炸弹大小,掼蛋进贡还贡,掼蛋抗贡,逢人配,掼蛋过A,掼蛋升级规则,竞技掼蛋竞赛规则',
    tldr:
      '掼蛋用两副扑克(108 张),四人两两结对、对家为搭档,每人 27 张,逆时针出牌,谁先出完谁是上游。只有上游一方升级:搭档二游升 3 级、三游升 2 级、下游升 1 级;从 2 打起,打到 A 并「过 A」(一家上游且搭档不是下游)获胜。牌型由大到小:四大天王 > 六张及以上炸弹 > 同花顺 > 五张炸弹 > 四张炸弹 > 普通牌型;红心级牌可当任意牌(王除外)使用。',
    updated: UPDATED,
    blocks: [
      {
        h2: '基本设置',
        ul: [
          '用牌:两副扑克共 108 张(含 4 张王)。',
          '人数:四人参与,两两结对,坐对家的两人是搭档。',
          '抓牌:每人 27 张(全手牌),按逆时针方向依次抓牌、出牌。',
          '级数:从 2 到 A 共 13 级,每轮第一副从 2 级打起。',
          '级牌:牌点与当前所打级数相同的牌(共 8 张),首副牌级牌为 2。红心级牌俗称「逢人配」,可替代除大小王以外的任意牌参与组合。',
        ],
      },
      {
        h2: '牌点大小',
        p: [
          '由大到小:大王 > 小王 > 级牌 > A > K > Q > J > 10 > 9 > 8 > 7 > 6 > 5 > 4 > 3 > 2。A 在组成顺子、三连对、三同连张、同花顺时也可当最小的 1 用(如 A2345)。所有牌型都不比花色。',
        ],
      },
      {
        h2: '牌型一览',
        table: {
          head: ['牌型', '组成', '示例'],
          rows: [
            ['单张', '任意一张牌', '2'],
            ['对子', '两张牌点相同的牌(含两张大王、两张小王)', '22'],
            ['三同张', '三张牌点相同', '222'],
            ['三带对', '一个三同张带一个对子,只比三同张大小', '222+33'],
            ['三连对(木板)', '三个牌点相邻的对子', '22+33+44'],
            ['三同连张(钢板)', '两个牌点相邻的三同张', '222+333'],
            ['顺子', '五个牌点相邻的单张', '34567'],
            ['炸弹', '四张及以上牌点相同', '8888、33333'],
            ['同花顺', '花色相同的顺子', '红桃 34567'],
            ['四大天王', '大王、小王各两张', '大王大王小王小王'],
          ],
        },
      },
      {
        h2: '炸弹与不同牌型的大小',
        p: [
          '不同牌型之间:四大天王 > 六张及以上的炸弹 > 同花顺 > 五张炸弹 > 四张炸弹 > 普通牌型(单张、对子、三同张、三带对、三连对、三同连张、顺子)。',
          '炸弹之间张数多者大,张数相同再比牌点。普通牌型只能被同类型更大的牌或炸弹类压制。例:四张 8 能压任何顺子,但会被五张 3 压住;同花顺又能压五张炸弹;六张炸弹压同花顺;四大天王最大。',
        ],
      },
      {
        h2: '出牌规则',
        ul: [
          '逆时针出牌。领出牌的人可以出任意牌型,其余人须用同牌型更大的牌或炸弹压制,也可以不出。',
          '连续三人不出,这一圈结束,由最后出牌的人重新领出。',
          '一家出完后,由其搭档接风领出(竞赛规则另有细则时以规程为准)。',
          '报牌:出完一手后手中剩 10 张及以下,须报出剩余张数。',
        ],
      },
      {
        h2: '升级规则',
        table: {
          head: ['上游一方的搭档名次', '升级数'],
          rows: [
            ['二游(对方「双下」)', '升 3 级'],
            ['三游', '升 2 级'],
            ['下游', '升 1 级'],
          ],
        },
        p: [
          '只有上游一方可以升级。A 级必打,「过 A」指打 A 级的一方一家为上游,且其搭档不是下游。',
        ],
      },
      {
        h2: '进贡、还贡与抗贡',
        ul: [
          '进贡:从第二副牌起,上副牌的下游把全手牌中最大的一张(红心级牌除外)进贡给上游。',
          '还贡:上游还给下游一张牌点不超过 10 的牌;若全手牌都大于 10,还最小的一张。首圈由进贡者领出。',
          '双贡:上副牌出现双下时,双下方两人都要进贡;上游取较大的贡牌,其搭档取较小的,并对应还牌。还牌后由进贡给上游的人领出。',
          '抗贡:下游(或双下方)在进贡前抓到两个大王,可以抗贡,不再进贡,首圈由上游领出。',
        ],
      },
      {
        h2: '比赛中怎么计成绩',
        p: [
          '休闲打法看谁先过 A;正式比赛通常按轮次或时间计分,把每副的升级数换算成胜负分、场分、级差分等再排名。办比赛时可直接用灵缇赛事系统扫码录入每副的获胜方与升级数,系统自动计算排名,详见「掼蛋比赛计分与排名口径」。',
        ],
      },
    ],
    faq: [
      { q: '掼蛋最大的牌型是什么?', a: '四大天王(两张大王加两张小王)最大,其次是六张及以上的炸弹、同花顺、五张炸弹、四张炸弹。' },
      { q: '同花顺和炸弹哪个大?', a: '同花顺大于五张及四张炸弹,但小于六张及以上的炸弹。' },
      { q: '红心级牌(逢人配)能当什么用?', a: '红心级牌可以替代除大王、小王以外的任意牌,参与顺子、炸弹、同花顺等各种牌型的组合。' },
      { q: '什么情况可以抗贡?', a: '需要进贡的一方(下游,或双下的两人)在进贡前抓到两个大王,就可以抗贡,不用进贡。' },
      { q: '还贡可以还什么牌?', a: '上游还给下游一张牌点不超过 10 的牌;如果手里的牌都大于 10,就还最小的一张。' },
      { q: '怎样才算过 A?', a: '打 A 级的一方必须有一家拿到上游,并且搭档不是下游,才算过 A。' },
    ],
    related: [
      ['竞技掼蛋竞赛规则全文', '/rules/jingji-guandan-2022'],
      ['江苏省掼蛋竞赛规则', '/rules/jiangsu-2023'],
      ['掼蛋计分与排名口径', '/guide/guandan-scoring'],
      ['如何组织一场掼蛋比赛', '/guide/how-to-run-guandan'],
      ['团团转规则', '/guide/tuantuanzhuan'],
    ],
  },
  {
    slug: 'swiss-system',
    title: '瑞士制编排规则详解:配对三原则、首轮四法、轮空与排名 · 灵缇办赛指南',
    h1: '瑞士制(瑞士移位)编排规则详解',
    description:
      '瑞士制是按成绩动态配对的赛制:每轮让成绩相近的对手相遇、不重复碰面、同俱乐部回避。本文讲清配对三原则、首轮四种起始配对、轮空处理、需要打几轮以及同分如何排名。',
    keywords: '瑞士制,瑞士移位,瑞士轮,掼蛋瑞士制,瑞士制编排,配对原则,轮空,对手分,Buchholz,掼蛋编排',
    tldr:
      '瑞士制每一轮都按当前积分排名,把名次相近、且之前没交过手的队伍配成一桌;打完若干轮后按「胜负分 → 胜场数 → 场分 → 级差分 → 对手积分」排名。它不需要所有队两两打满,几轮就能区分出真实实力,是掼蛋比赛最主流的编排方式。',
    updated: UPDATED,
    blocks: [
      {
        h2: '什么是瑞士制',
        p: [
          '瑞士制(Swiss System)起源于国际象棋,是一种「积分赛」:参赛者不会被淘汰,每轮都参赛,但对手由系统按当前成绩动态安排。和循环赛相比,它用少得多的轮次就能排出可信的名次;和淘汰赛相比,它让每支队都打满全部轮次。',
          '掼蛋、团体掼蛋、匹克球双打等项目都普遍采用瑞士制。在灵缇赛事系统里,「双人赛(组队赛)」默认就是瑞士制编排,是平台上创建量最多的赛制。',
        ],
      },
      {
        h2: '配对三原则',
        ol: [
          '成绩相近的对阵:每轮按当前排名,把名次相邻的队伍两两配对(第 1 对第 2、第 3 对第 4……)。',
          '对阵过的不再对阵:同一对手在整个赛程中只碰一次,系统自动避开重复对阵。',
          '同俱乐部回避:同一俱乐部/单位的队伍尽量不互打,避免「自家人」影响公平。',
        ],
        p: ['灵缇赛事系统在此之上还叠加了「种子不碰种子」和轮空自动处理,三条原则冲突时按上面的优先级依次放宽。'],
      },
      {
        h2: '首轮怎么配对(首轮四法)',
        p: ['第一轮还没有成绩,需要先选一种起始配对方式,之后每轮都按最新成绩重排:'],
        table: {
          head: ['方式', '示例(100 队)', '适用场景'],
          rows: [
            ['相邻配对', '1 对 2、3 对 4……', '按报名顺序或种子顺序直接开打'],
            ['首尾配对', '1 对 100、2 对 99……', '有种子排序,希望强弱先分开'],
            ['拦腰配对', '1 对 51、2 对 52……', '种子在前半区,避免首轮实力悬殊太大'],
            ['随机配对', '电脑随机抽签', '没有种子信息的群众赛、友谊赛'],
          ],
        },
      },
      {
        h2: '轮空怎么处理',
        p: [
          '参赛单位为奇数时,每轮会有一支队没有对手,称为轮空(bye)。轮空一方按规则计一个默认成绩,并且同一支队不会重复轮空。灵缇赛事系统会自动选出轮空队并记分,裁判无需手工处理。',
        ],
      },
      {
        h2: '瑞士制需要打几轮',
        p: [
          '经验上,要从 N 支队里区分出唯一领先者,轮数至少约为 log₂N:16 队约 4 轮,32 队约 5 轮,64 队约 6 轮,128 队约 7 轮。实际办赛还要考虑时长——掼蛋每轮通常 40–60 分钟,地方赛常见 4–7 轮。',
          '轮数太少,前几名可能同分扎堆;轮数接近队数时,瑞士制就近似于循环赛,失去了「省轮次」的意义。',
        ],
      },
      {
        h2: '同分如何排名',
        p: ['灵缇赛事系统的掼蛋瑞士制默认按以下顺序依次比较(可在「比赛设置 → 排名依据」中拖动调整顺序):'],
        table: {
          head: ['顺序', '指标', '含义'],
          rows: [
            ['1', '胜负分', '每局胜 2 / 平 1 / 负 0 累加,排名第一依据'],
            ['2', '胜场数', '赢下的局数'],
            ['3', '场分', '每局本方原始得分累加,反映赢得多大'],
            ['4', '级差分', '反映赢得是否彻底(头游得分高、末游扣分)'],
            ['5', '对手积分(Buchholz)', '你所有对手的得分之和,同分时对手更强者排前'],
          ],
        },
      },
      {
        h2: '瑞士制和复式赛的区别',
        p: [
          '瑞士制比的是「谁赢得多」:各桌现发现打,牌不一样;复式赛比的是「同一副牌谁打得好」:多桌打同一副牌再横向比较,用来消除牌运。两者可以结合——灵缇支持「瑞士偏差分复式赛」,既按成绩动态配对,又用同一副牌比较。',
        ],
      },
    ],
    howto: {
      name: '在灵缇赛事系统办一场瑞士制掼蛋赛',
      steps: [
        '登录 hy.lingti.com,进入「比赛列表」点「创建比赛」,赛制选「双人赛(组队赛)」。',
        '在「队伍管理」中手动新增或用 Excel 批量导入参赛队,可标记种子与所属俱乐部。',
        '在「比赛设置」里选择首轮配对方式、轮数和排名依据。',
        '每轮点「编排」自动生成桌次,选手扫码查看桌号。',
        '选手或裁判扫码录入每局比分,本轮结算后系统自动按成绩编排下一轮。',
        '全部轮次结束后查看总排名,可一键导出成绩、生成获奖证书或投屏展示。',
      ],
    },
    faq: [
      { q: '瑞士制会不会两个队打两次?', a: '不会。「对阵过的不再对阵」是瑞士制的基本原则,系统编排时会自动避开已交手的对手;只有在剩余队伍极少、无法避开时才会放宽。' },
      { q: '瑞士制第一名一定是全胜吗?', a: '不一定。轮数足够时第一名通常全胜或只输一场;同胜负分时按胜场数、场分、级差分、对手积分依次比较。' },
      { q: '同俱乐部的队一定不会相遇吗?', a: '系统会尽量回避,但当回避会导致重复对阵或无法配对时,优先保证「不重复对阵」,此时同俱乐部可能相遇。' },
      { q: '奇数队可以打瑞士制吗?', a: '可以。每轮会有一支队轮空并计默认成绩,系统保证同一支队不会重复轮空。' },
    ],
    related: [
      ['掼蛋计分与排名口径', '/guide/guandan-scoring'],
      ['如何组织一场掼蛋比赛', '/guide/how-to-run-guandan'],
      ['复式赛是什么', '/guide/duplicate'],
      ['瑞士制词条', '/baike/swiss'],
      ['对手积分(Buchholz)词条', '/baike/buchholz'],
    ],
  },
  {
    slug: 'tuantuanzhuan',
    title: '团团转(转蛋赛)规则:每副换搭档、6 分制与场分 3/2/1/0 · 灵缇办赛指南',
    h1: '团团转(转蛋赛)规则与记分方法',
    description:
      '团团转又叫转蛋赛,是按个人计分、每打一副都换搭档的掼蛋赛制。本文讲清团团转的排桌方法、换搭档规则、推荐的 6 分制记分,以及为什么正式赛应开启场分(3/2/1/0)。',
    keywords: '团团转,转蛋赛,团团转规则,团团转记分,6分制,场分,掼蛋个人赛,换搭档,团团转编排',
    tldr:
      '团团转是掼蛋的个人赛玩法:个人报名、个人计分,每打一副牌都换一次搭档。每副按名次在两组搭档间分 6 分(双上 6:0、头游+三游 5:1、头游+末游 4:2);正式赛建议开启「场分」,每副按本桌积分高低给 3/2/1/0 分,以场分为排名第一依据,杜绝协商抬分。',
    updated: UPDATED,
    blocks: [
      {
        h2: '团团转是什么',
        p: [
          '团团转,又叫「转蛋赛」,是一种掼蛋个人赛。它按个人计分、每打一副都要换搭档——你不会一直和固定队友绑在一起,一场下来能和全场更多人配合过、也对抗过,成绩只取决于自己的发挥。',
          '在灵缇赛事系统中,团团转是创建量排名前三的赛制,也支持「偏差分团团转复式赛」和线上团团转。',
        ],
      },
      {
        h2: '赛程与排桌(示例)',
        ul: [
          '比赛共 3 轮,每轮 8 副牌,每轮约 50 分钟;个人报名,每副记个人得分,固定打 2。',
          '首轮:电脑随机派位,按配号就座。',
          '第 2 轮起:按前几轮总分排序,1–4 名一桌、5–8 名一桌,以此类推。',
          '提前打完 8 副即结束本轮。',
        ],
      },
      {
        h2: '换搭档规则(核心)',
        p: [
          '每轮首副由东家洗牌、南家切牌并翻一张明牌(翻到大小王、红心 2 重翻),从翻牌人起逆时针数出首抓牌者,抓到明牌的人先出牌。',
          '抓到明牌的人座位不动;抓完牌后,摸到与明牌相同那张牌的人换到明牌人的对家——这就是「每副换搭档」的来历。若另一张相同牌也在明牌人手里,则本副原座不动。',
          '团团转不进贡、不还牌,固定打 2;手中剩 10 张(含)以内要主动报牌张数,只报一次。',
        ],
      },
      {
        h2: '记分:推荐 6 分制',
        p: ['每副一桌 4 人分成两组搭档,按名次组合在两组间分配 6 分(本组 : 对方):'],
        table: {
          head: ['名次组合', '本组得分', '对方得分'],
          rows: [
            ['头游 + 二游(双上)', '6', '0'],
            ['头游 + 三游', '5', '1'],
            ['头游 + 四游', '4', '2'],
          ],
        },
      },
      {
        h2: '场分:为什么正式赛必须开',
        p: [
          '只看小分(积分)排名有个隐患:团团转每副都换对手,桌子强弱不一,碰到弱桌的人小分反而更高;靠后的桌次还可能协商抬分。',
          '「场分」按每副在本桌的名次给分:积分从高到低依次得 3、2、1、0 分(同分平均,如并列第 2、3 名各得 1.5)。场分作为排名第一依据,小分只在场分相同时参考——想拿高名次,就必须在本桌真刀真枪打赢。',
          '在灵缇中设置:比赛设置 → VIP 专区 →「团团转场分」填 3,2,1,0(也可自定义如 5,3,1,0)。比赛中途补填也没关系,保存后系统自动按新口径重算全场,原始积分不变,完全可逆。',
        ],
      },
    ],
    howto: {
      name: '在灵缇赛事系统办一场团团转',
      steps: [
        '点「创建比赛」,赛制选「团团转」;第一次办可以把「是否为测试赛」选成「是」先演练。',
        '录入或导入个人选手名单,选手也可扫码报名。',
        '在比赛设置 → VIP 专区填写团团转场分 3,2,1,0。',
        '首轮随机派位,之后每轮按总分自动排桌。',
        '每副由西家记分、北家复查,手机扫码录分,系统实时出榜。',
        '三轮结束后按场分为先、小分次之出最终排名。',
      ],
    },
    faq: [
      { q: '团团转为什么我小分最高却不是第一?', a: '因为开启了场分:排名先看场分(每副在本桌的名次分),小分只在场分并列时才比较。小分高可能只是碰到了弱桌。' },
      { q: '团团转可以用 5 分制或 10 分制吗?', a: '可以按需选择,但我们最推荐 6 分制:档次拉得更清楚、计算也简单。' },
      { q: '团团转一般打几轮?', a: '常见 3 轮、每轮 8 副、每轮约 50 分钟;人数多可适当增加轮数。' },
      { q: '忘了设置场分怎么办?', a: '回到比赛设置补填 3,2,1,0 并保存,系统会自动重算全场排名,不用重开比赛。' },
    ],
    related: [
      ['为什么要看场分(资讯)', '/news'],
      ['掼蛋计分与排名口径', '/guide/guandan-scoring'],
      ['瑞士制编排规则', '/guide/swiss-system'],
      ['团团转规则词条', '/baike/ttzgz'],
    ],
  },
  {
    slug: 'duplicate',
    title: '复式赛是什么?复式掼蛋/复式桥牌的规则、移位与计分 · 灵缇办赛指南',
    h1: '复式赛是什么:同一副牌,才配论英雄',
    description:
      '复式赛让同一副牌在多桌重复打,再横向比较谁打得更好,从而消除牌运。本文介绍队式开闭室、米切尔、豪厄尔三种复式形式、Matchpoint 计分,以及如何用复式发牌机办复式掼蛋、复式桥牌。',
    keywords: '复式赛,复式掼蛋,复式桥牌,复式赛规则,开闭室,米切尔移位,豪厄尔移位,Matchpoint,复式发牌机,偏差分',
    tldr:
      '复式赛(Duplicate)把同一副牌交给不同的人在多张桌上各打一遍,再比较大家在这副牌上的成绩:好牌大家都拿过,烂牌大家也都扛过,运气被抵消,剩下的差距只来自打法。常见形式有队式开闭室(最简单)、米切尔移位(南北/东西双冠军)和豪厄尔移位(单冠军),计分多用 Matchpoint 横比。',
    updated: UPDATED,
    blocks: [
      {
        h2: '复式赛的原理',
        p: [
          '复式源自桥牌的百年实践:比赛前把牌预先发好成若干「牌集(board)」,每副牌在多桌按固定方向传递(relay),每桌都打一遍。最后把同一副牌在各桌的成绩放在一起比,谁比别人多打出多少,就是真实的技术差距。',
          '复式不只属于桥牌。掼蛋、双升、斗地主都可以办复式——灵缇复式发牌机是目前同时覆盖桥牌与掼蛋的复式发牌机,把复式赛的门槛降到「一台机器」。',
        ],
      },
      {
        h2: '三种常见复式形式',
        table: {
          head: ['形式', '怎么打', '冠军数', '适合'],
          rows: [
            ['队式开闭室', '两队各 4 人分坐开室、闭室,座位对换,同一副牌两桌成绩相加比较', '按队对抗', '团体对抗、入门复式'],
            ['米切尔移位', 'n 桌 2n 对,南北固定、东西每轮上移一桌,牌集传递', '2 个(南北线 + 东西线)', '人多、要快速出成绩'],
            ['豪厄尔移位', 'm 对 m/2 桌,圆桌轮转,任意两对恰好相遇一次', '1 个(全场统一)', '桌数少、要唯一总冠军'],
          ],
        },
      },
      {
        h2: 'Matchpoint 配对分怎么算',
        p: [
          '同一副牌在各桌的成绩两两比较:胜 2、平 1、负 0;每副满分 =(打此副的桌数 − 1)× 2,最后汇总成百分比排名。拱猪、华牌这类「分低者胜」的项目,系统可反向比较。',
          '掼蛋复式还常用「偏差分」:把本桌成绩与这副牌全场平均成绩的差值作为得分,同样能消除牌运。灵缇支持瑞士偏差分复式赛和偏差分团团转复式赛。',
        ],
      },
      {
        h2: '办复式赛的难点与解决',
        ul: [
          '备牌:传统要人工预发几十副牌,容易插错方位、多牌少牌——复式发牌机按牌谱自动发牌,还能杜绝牌手串通打过的牌。',
          '移位:米切尔、豪厄尔的移位表复杂,双数桌米切尔还要「跳桌」避免撞车——灵缇赛事系统自动生成移位表。',
          '计分:每副要横向比较、汇总百分比——系统扫码录分后自动计算,可追溯到每副牌、每个组合。',
          '牌运:随机发牌也可能出现一边倒的牌——发牌机的「均衡牌力牌」在一场实测中把极端牌局占比从约 67% 降到约 11%。',
        ],
      },
    ],
    faq: [
      { q: '复式赛和普通比赛最大的区别是什么?', a: '普通比赛各桌牌不同,输赢很大程度受牌运影响;复式赛多桌打同一副牌再比较,消除了牌运,比的是同牌下谁打得更好。' },
      { q: '掼蛋可以办复式赛吗?', a: '可以。灵缇支持四人队式开闭室复式赛、米切尔复式赛、瑞士偏差分复式赛、偏差分团团转复式赛、单人复式循环赛等多种掼蛋复式赛制。' },
      { q: '办复式赛一定要发牌机吗?', a: '不是必须,也可以人工预发牌;但发牌机能自动按牌谱发牌、避免插错方位,并支持均衡牌力,大幅降低办赛难度。' },
      { q: '米切尔和豪厄尔怎么选?', a: '要南北、东西两个冠军、人数多选米切尔;桌数少、只要一个总冠军选豪厄尔。详见米切尔与豪厄尔对比。' },
    ],
    related: [
      ['米切尔与豪厄尔移位制对比', '/guide/mitchell-howell'],
      ['灵缇复式发牌机', '/fushiji'],
      ['复式赛词条', '/baike/duplicate'],
      ['Matchpoint 词条', '/baike/matchpoint'],
      ['赛制一览', '/rules/formats'],
    ],
  },
  {
    slug: 'mitchell-howell',
    title: '米切尔移位与豪厄尔移位对比:桌数、轮数、冠军数怎么选 · 灵缇办赛指南',
    h1: '米切尔移位制 vs 豪厄尔移位制',
    description:
      '米切尔移位南北固定、东西流动,产生南北线与东西线两个冠军;豪厄尔移位圆桌轮转,产生全场唯一冠军。本文对比两种复式移位制的桌数要求、轮数、完全/不完全米切尔与跳桌规则。',
    keywords: '米切尔移位,豪厄尔移位,豪威尔移位,完全米切尔,不完全米切尔,跳桌,复式移位,桥牌移位,复式掼蛋',
    tldr:
      '米切尔:n 桌 2n 对,南北不动、东西每轮上移一桌,单数桌打满 n 轮(完全米切尔),双数桌只打 n−1 轮并中途跳桌(不完全米切尔),南北线和东西线各出一个冠军。豪厄尔:m 对(偶数)m/2 桌,圆桌轮转、任意两对恰好相遇一次,最多 m−1 轮,全场一个冠军。',
    updated: UPDATED,
    blocks: [
      {
        h2: '一张表看懂区别',
        table: {
          head: ['对比项', '米切尔移位', '豪厄尔移位'],
          rows: [
            ['冠军数', '2 个(南北线 + 东西线)', '1 个(全场统一)'],
            ['人员要求', '桌数 ≥ 3,单双数桌均可', '对数为偶数且 ≥ 4(桌数 = 对数 / 2)'],
            ['轮数', '单数桌 n 轮;双数桌 n−1 轮', '最多 m−1 轮(m = 对数)'],
            ['谁移动', '南北固定,东西每轮上移一桌', '固定一号位,其余绕圈轮转'],
            ['适合场景', '人多、要快速出成绩', '桌数少、要评出唯一总冠军'],
          ],
        },
      },
      {
        h2: '完全米切尔与不完全米切尔',
        p: [
          '单数桌时,东西对一路顺移即可跑满 n 轮:每个东西对遇齐全部南北对、每对打齐全部 n 副牌且不重复,称为完全米切尔。',
          '双数桌时,若跑满 n 轮会「撞车」(同一对重复打同一副牌),所以要减掉一轮只打 n−1 轮,并在打到一半时让东西对多跳一桌(skip)破除奇偶,称为不完全米切尔。',
        ],
      },
      {
        h2: '豪厄尔怎么排',
        p: [
          'm 对、m/2 桌,用圆桌轮转法:固定一号位,其余对子绕圈,保证任意两对在整个赛程中恰好相遇一次。每轮各桌打同一副新牌,用 Matchpoint 横比,所有对子并入同一名次表。',
        ],
      },
    ],
    faq: [
      { q: '6 桌适合用米切尔吗?', a: '可以,6 桌是双数桌,使用不完全米切尔打 5 轮,中途东西对跳桌一次避免撞车。' },
      { q: '为什么米切尔有两个冠军?', a: '南北对始终坐南北、东西对始终坐东西,两条线各自比较同一副牌的成绩,互不直接对比,所以分别产生南北线冠军和东西线冠军。' },
      { q: '灵缇能自动生成移位表吗?', a: '能。创建「米切尔复式赛」等复式赛制后,系统按桌数自动生成每轮的桌次与牌集流向,并自动计算 Matchpoint。' },
    ],
    related: [
      ['复式赛是什么', '/guide/duplicate'],
      ['米切尔移位词条', '/baike/mitchell'],
      ['豪厄尔移位词条', '/baike/howell'],
      ['赛制一览', '/rules/formats'],
    ],
  },
  {
    slug: 'guandan-scoring',
    title: '掼蛋比赛怎么计分排名:胜负分、场分、级差分、对手分一文讲清 · 灵缇办赛指南',
    h1: '掼蛋比赛计分与排名口径',
    description:
      '掼蛋比赛常用胜负分、胜场数、场分、级差分、对手积分等指标排名。本文解释每个指标的含义与计算方式、默认排名顺序、升级规则,以及如何在灵缇赛事系统里调整排名依据。',
    keywords: '掼蛋计分,掼蛋排名,胜负分,场分,级差分,对手分,小分,掼蛋升级规则,双上,掼蛋比赛规则',
    tldr:
      '掼蛋比赛默认按「胜负分 → 胜场数 → 场分 → 级差分 → 对手积分」排名:胜负分是每局胜 2 / 平 1 / 负 0 的累加;场分是每局原始得分累加;级差分反映赢得是否彻底;对手积分是所有对手得分之和,用于同分时区分。顺序可在比赛设置中自定义。',
    updated: UPDATED,
    blocks: [
      {
        h2: '掼蛋怎么升级',
        p: ['一副牌打完按出完牌的先后排名(头游、二游、三游、末游),头游一方升级:'],
        table: {
          head: ['头游一方的名次组合', '升级数'],
          rows: [
            ['头游 + 二游(双上)', '升 3 级'],
            ['头游 + 三游', '升 2 级'],
            ['头游 + 末游', '升 1 级'],
          ],
        },
      },
      {
        h2: '常用排名指标',
        table: {
          head: ['指标', '计算方式', '反映什么'],
          rows: [
            ['胜负分', '每局胜 2 / 平 1 / 负 0 累加', '赢了几局,排名第一依据'],
            ['胜场数', '赢下的局数(胜负分 ÷ 2 累加)', '胜率'],
            ['场分', '每局本方原始得分累加', '赢得多大、输得多少'],
            ['级差分', '每局按名次计分累加,头游得分高、末游扣分', '赢得是否彻底'],
            ['对手积分', '你所有对手的场分之和(Buchholz 思路)', '对手强度,同分时对手更强者排前'],
          ],
        },
      },
      {
        h2: '为什么「场分为先」更公平',
        p: [
          '只按小分(净胜分)排名,容易受对手强弱和牌运影响,也给协商抬分留了空间。把胜负/名次类的「场分」放在第一顺位,小分只在并列时作为决胜,能让排名更看重「这一局你赢没赢」。瑞士制天然以胜负分为第一顺位;团团转建议开启 3/2/1/0 场分。',
        ],
      },
      {
        h2: '在灵缇调整排名依据',
        p: [
          '进入比赛设置 → 签到/记分设置 →「排名依据」,可以拖动或点「置顶 / 上移 / 下移 / 置底」调整各指标顺序,也可以增删指标。保存后,所有已打开的排名页、投屏页会实时按新顺序重排。',
        ],
      },
    ],
    faq: [
      { q: '掼蛋比赛平局怎么算?', a: '胜负分按平局计 1 分。是否允许平局、如何判平由赛事规程决定,系统按录入的比分自动计算。' },
      { q: '掼蛋比赛同分怎么排名?', a: '按排名依据顺序依次比较,默认胜负分相同比胜场数,再比场分、级差分、对手积分。' },
      { q: '可以只按场分排名吗?', a: '可以。在「排名依据」里把场分置顶或删掉其他指标即可,保存后立即生效。' },
    ],
    related: [
      ['瑞士制编排规则', '/guide/swiss-system'],
      ['团团转规则', '/guide/tuantuanzhuan'],
      ['掼蛋竞赛规则全文', '/rules/g/guandan'],
      ['排名优先级词条', '/baike/rank-order'],
      ['级差分词条', '/baike/rank-up-score'],
    ],
  },
  {
    slug: 'how-to-run-guandan',
    title: '如何组织一场掼蛋比赛:从报名、编排、记分到颁奖的完整流程 · 灵缇办赛指南',
    h1: '如何组织一场掼蛋比赛(完整流程清单)',
    description:
      '办一场掼蛋比赛需要确定赛制、报名收费、签到、编排桌次、计时、记分、出成绩和颁奖。本文按时间顺序给出完整清单,并说明每一步在灵缇赛事系统里怎么一键完成。',
    keywords: '掼蛋比赛怎么组织,掼蛋比赛流程,如何办掼蛋比赛,掼蛋比赛编排软件,掼蛋记分软件,掼蛋报名,掼蛋比赛方案,掼蛋赛事系统',
    tldr:
      '办掼蛋比赛的标准流程是:定赛制(双人瑞士制最常见,个人赛用团团转)→ 发布报名(可在线收费)→ 现场扫码签到 → 系统一键编排桌次 → 统一计时开打 → 选手扫码录分 → 每轮结算自动排下一轮 → 出总排名、发证书、投屏颁奖。用灵缇赛事系统,一个裁判长就能管好上百桌。',
    updated: UPDATED,
    blocks: [
      {
        h2: '赛前一周:确定赛制和规程',
        ul: [
          '参赛形式:双人组队赛(最常见,瑞士制)、个人赛(团团转)、四人团体赛、复式赛。',
          '规模与时长:按「每轮 40–60 分钟」估算轮数;64 队一般 5–6 轮。',
          '排名规则:胜负分、场分、级差分、对手分的顺序;团团转是否开启场分。',
          '规则依据:可参考《竞技掼蛋竞赛规则》《江苏省掼蛋竞赛规则》等,灵缇规则库收录了全文。',
        ],
      },
      {
        h2: '报名与收费',
        ul: [
          '在灵缇创建比赛后生成报名链接/二维码,发到群里即可在线报名。',
          '可设置报名费在线微信支付,自动对账,无需手工收款。',
          '主办方也可以用 Excel 批量导入已收集的名单。',
        ],
      },
      {
        h2: '比赛当天:签到、编排、计时',
        ul: [
          '扫码签到:选手扫比赛二维码,用报名手机号查到自己的队伍完成签到;严格签到可只允许手机号查询。',
          '一键编排:每轮按瑞士制规则自动生成桌次,种子、同俱乐部回避自动处理。',
          '大屏投屏:桌次表、倒计时、实时排名可投到大屏,选手不再挤着看纸质表。',
        ],
      },
      {
        h2: '记分与结算',
        ul: [
          '选手扫桌上的二维码录入本局比分,双方确认;裁判长后台可审核、修改,所有改分留痕可查。',
          '本轮全部录完后一键结算,系统按排名依据自动计算排名并编排下一轮。',
          '出现争议时可查看每局的录分历史,成绩只加不减、全程可追溯。',
        ],
      },
      {
        h2: '赛后:排名、证书与复盘',
        ul: [
          '导出总排名 Excel/PDF,公示成绩。',
          '一键生成获奖证书和比赛海报,便于发朋友圈、公众号宣传。',
          '联赛可把多站成绩累加成联赛总榜和团体榜。',
        ],
      },
    ],
    howto: {
      name: '用灵缇赛事系统组织一场掼蛋比赛',
      steps: [
        '在 hy.lingti.com 注册登录,点「创建比赛」,选择赛制并填写名称、时间、地点。',
        '分享报名二维码或导入 Excel 名单,按需开启在线收费。',
        '比赛当天让选手扫码签到。',
        '每轮点「编排」生成桌次,开启倒计时并投屏。',
        '选手扫码录分,裁判长审核后结算本轮。',
        '所有轮次结束后导出排名、生成证书,完成颁奖。',
      ],
    },
    faq: [
      { q: '办掼蛋比赛用什么软件编排?', a: '可以用灵缇赛事系统(hy.lingti.com),网页和微信小程序都能用,支持瑞士制、团团转、循环赛、复式赛等 20 多种棋牌赛制,一键编排、扫码记分、自动排名。' },
      { q: '掼蛋比赛一个裁判能管多少桌?', a: '使用扫码录分后,裁判长主要负责审核与处理争议;平台上常见单场几十到上百桌的比赛。' },
      { q: '灵缇赛事系统收费吗?', a: '基础功能免费,双人赛等常用赛制可直接使用;部分高级赛制和功能需要 VIP 或灵缇点。' },
      { q: '没有网络可以用吗?', a: '录分需要联网;选手用手机流量或现场 Wi-Fi 即可,网页和小程序都可录分。' },
    ],
    related: [
      ['瑞士制编排规则', '/guide/swiss-system'],
      ['团团转规则', '/guide/tuantuanzhuan'],
      ['掼蛋计分与排名口径', '/guide/guandan-scoring'],
      ['使用手册', '/manual'],
      ['掼蛋竞赛规则', '/rules/g/guandan'],
    ],
  },
  {
    slug: 'group-knockout',
    title: '小组循环 + 淘汰赛怎么编排:匹克球/网球/羽毛球锦标赛分组与晋级 · 灵缇办赛指南',
    h1: '小组循环 + 淘汰赛编排方法(球拍类)',
    description:
      '小组循环加淘汰赛是匹克球、网球、羽毛球、乒乓球锦标赛最常用的赛制。本文讲清如何蛇形分组、组内场次怎么算、每组取几名晋级、淘汰赛如何配对,以及 16 队、32 队的推荐配置。',
    keywords: '小组淘汰赛,小组循环淘汰赛,匹克球比赛编排,网球比赛编排,羽毛球比赛编排,乒乓球比赛编排,蛇形分组,单败淘汰,锦标赛赛制',
    tldr:
      '先按种子蛇形分组,组内单循环(每组 N 队打 N(N−1)/2 场),按「胜场分 → 总得分 → 对手总分」取每组前几名进入单败淘汰赛,依次打 1/4 决赛、半决赛、决赛和季军赛。16 队常用 4 组 × 4 队、每组前 2 晋级 8 强。',
    updated: UPDATED,
    blocks: [
      {
        h2: '为什么用「小组 + 淘汰」',
        p: [
          '纯循环赛最公平但场次太多(16 队要 120 场),纯淘汰赛最快但一场失误就出局。小组循环保证每队至少打 N−1 场,淘汰赛阶段又能快速决出冠亚季,是业余和专业锦标赛的通用折中。',
        ],
      },
      {
        h2: '分组与场次',
        table: {
          head: ['参赛队数', '推荐分组', '组内场次(合计)', '晋级方式', '淘汰赛规模'],
          rows: [
            ['8', '2 组 × 4 队', '6 × 2 = 12 场', '每组前 2', '4 强'],
            ['16', '4 组 × 4 队', '6 × 4 = 24 场', '每组前 2', '8 强'],
            ['24', '4 组 × 6 队', '15 × 4 = 60 场', '每组前 2', '8 强'],
            ['32', '8 组 × 4 队', '6 × 8 = 48 场', '每组前 2', '16 强'],
          ],
        },
        p: ['组内场次 = N(N−1)/2。蛇形分组(1、8、9、16 号种子进 A 组……)让各组实力均衡;同单位选手可尽量分开。'],
      },
      {
        h2: '小组排名与晋级',
        p: [
          '组内按胜场分 → 总得分 → 对手总分排序,取前 N 名进入淘汰赛。淘汰赛按种子位对称配对,不同小组的头名分在不同半区,避免同组队伍过早再遇;半决赛负者打季军赛,决出冠、亚、季、殿军。',
        ],
      },
      {
        h2: '计分规则可配置',
        ul: [
          '每局目标分:11 / 15 / 21 分。',
          '领先分:领先 1 分或 2 分获胜,可设封顶分。',
          '局数:单局、三局两胜、五局三胜。',
          '计分制:每球得分(rally)或发球得分(side-out),匹克球两种都支持。',
          '单打 / 双打一键切换。',
        ],
      },
    ],
    howto: {
      name: '在灵缇创建一场球拍类小组淘汰赛',
      steps: [
        '访问 hy.lingti.com/match 点「创建比赛」,切到「球拍类赛事」,选「球拍类小组淘汰赛」。',
        '设置小组数(2/4/8)和每组晋级数(1/2/4),以及目标分、局数、单双打。',
        '录入选手(支持 Excel 导入)并创建场地。',
        '点「一键编排」生成全部小组对局,按组 Tab 查看。',
        '录入比分,小组赛结束后系统自动生成淘汰赛对阵,直至决出冠亚季。',
      ],
    },
    faq: [
      { q: '16 队怎么分组最合适?', a: '最常用 4 组 × 4 队,每组 6 场共 24 场小组赛,每组前 2 名进 8 强;也可 2 组 × 8 队或 8 组 × 2 队。' },
      { q: '小组同分怎么排?', a: '依次比较胜场分、总得分、对手总分。' },
      { q: '支持季军赛吗?', a: '支持,半决赛负者自动生成季军赛,系统判定冠、亚、季和第四名。' },
    ],
    related: [
      ['匹克球赛事', '/sports/pickleball'],
      ['网球/羽毛球/乒乓球赛事', '/sports/racquet'],
      ['循环赛编排', '/guide/round-robin'],
      ['匹克球国际竞赛规则', '/rules/pickleball-world-2024'],
    ],
  },
  {
    slug: 'round-robin',
    title: '循环赛怎么编排:场次计算、轮转表与团队分组循环赛 · 灵缇办赛指南',
    h1: '循环赛编排方法与场次计算',
    description:
      '循环赛让所有参赛者两两交手一次,N 队共 N(N−1)/2 场。本文给出场次速查表、单循环与分组循环的区别、轮转编排方法,以及团队分组循环赛「裁判长登分、一键出榜」的做法。',
    keywords: '循环赛,单循环,循环赛场次计算,循环赛编排,贝格尔编排,分组循环赛,团队分组循环赛,循环赛排名',
    tldr:
      '单循环中每支队与其他所有队各打一场,N 队共 N(N−1)/2 场、至少 N−1 轮(奇数队 N 轮,每轮一队轮空)。队数多时改为分组循环:分成若干小组各自循环,再出组内榜和总榜。',
    updated: UPDATED,
    blocks: [
      {
        h2: '场次速查',
        table: {
          head: ['队数', '总场次', '最少轮数'],
          rows: [
            ['4', '6', '3'],
            ['6', '15', '5'],
            ['8', '28', '7'],
            ['10', '45', '9'],
            ['12', '66', '11'],
            ['16', '120', '15'],
          ],
        },
      },
      {
        h2: '轮转编排',
        p: [
          '常用「固定一号位、其余绕圈」的轮转法:把队伍排成两行,第一个位置不动,其余每轮顺时针转一格,上下对位即为本轮对阵;奇数队时补一个「轮空」位。灵缇赛事系统的循环赛会自动生成全部轮次。',
        ],
      },
      {
        h2: '团队分组循环赛',
        p: [
          'X 支队分成 Y 个小组(每组 N 支),组内两两对抗一次,共 N(N−1)/2 场。每场由裁判长给双方各登一个整数比分(如 11 : 7),得分高者胜;系统实时汇总小组签表(队 × 队胜负网格)和全场总榜,默认按胜场数 → 总积分排名。',
          '支持自动蛇形分组、标记种子队尽量分到不同小组、自动补齐桌次。',
        ],
      },
    ],
    faq: [
      { q: '循环赛和瑞士制哪个好?', a: '队数少(约 8 队以内)且时间充足用循环赛最公平;队数多时用瑞士制,用约 log₂N 轮就能排出可信名次。' },
      { q: '循环赛同分怎么排?', a: '常用胜场数 → 总积分 → 相互胜负;灵缇可在「排名依据」中自定义顺序。' },
    ],
    related: [
      ['瑞士制编排规则', '/guide/swiss-system'],
      ['小组循环 + 淘汰赛', '/guide/group-knockout'],
      ['循环赛词条', '/baike/round-robin'],
    ],
  },
  {
    slug: 'league',
    title: '联赛怎么办:多站累计积分、团体榜与晋级决赛 · 灵缇办赛指南',
    h1: '联赛玩法:多站累计 · 团体对抗 · 晋级决赛',
    description:
      '联赛是用同一份名单连续办多站比赛,把每支队各站成绩累加成联赛总榜,并可出团体/单位榜、按名次晋级决赛。本文介绍联赛的核心机制、操作流程和最容易犯的错误。',
    keywords: '掼蛋联赛,联赛积分,多站赛,城市联赛,俱乐部月赛,巡回赛,团体榜,晋级决赛,联赛管理系统',
    tldr:
      '联赛 = 一串比赛的容器:各分站绑定到同一个联赛,参赛队都从统一的联赛名单生成,系统按队伍身份把各站成绩累加成联赛总分,还能按所属团体出团体榜,下一站可按名次取前 N 名打决赛。',
    updated: UPDATED,
    blocks: [
      {
        h2: '核心机制',
        ol: [
          '联赛是一串比赛的容器,多个分站/轮次绑定到同一联赛。',
          '统一的联赛名单:正式队员 + 候补队员 + 所属团体,各站从名单生成参赛队。',
          '联赛得分 = 各站成绩按队伍身份累加。',
          '每队归属俱乐部/单位,可出团体对抗榜。',
          '候补队员顶替上场成绩仍算本队;可按名次晋级取前 N 名打决赛。',
        ],
      },
      {
        h2: '最容易犯的错',
        ul: [
          '分站的队是手动新增或单独导入的 → 不计入联赛得分,必须走「从联赛名单添加参赛队」。',
          '新站又「创建新联赛」而不是「绑定联赛」→ 各站互不相干,累计不到一起。',
          '分站没结算完就看总榜 → 少算,先把各站各轮结算完。',
          '团体名没填 → 团体榜出不来。',
        ],
      },
    ],
    howto: {
      name: '在灵缇办一个多站联赛',
      steps: [
        '建第一站,赛制选「联赛」,左侧菜单出现「联赛设置」和「联赛得分」。',
        '在联赛设置里第一站点「创建新联赛」,之后每站点「绑定联赛」选同一个。',
        '手动或 Excel 导入联赛名单(队名、队员、手机号、候补、团体名)。',
        '每站在队伍管理里「从联赛名单添加参赛队」,可全部、按序号或按名次晋级。',
        '该站正常编排、比赛、计分、结算。',
        '进「联赛得分」查看全联赛累计排名与团体榜。',
      ],
    },
    faq: [
      { q: '联赛得分显示 0 怎么办?', a: '打开「联赛得分」页会自动重算;也可以在联赛设置点「统计联赛成绩」手动重算。确认各站已结算、参赛队是从联赛名单生成的。' },
      { q: '改了联赛名单会同步到已建的分站吗?', a: '不会,名单是模板,建议定稿后再各站生成参赛队。' },
    ],
    related: [
      ['如何组织一场掼蛋比赛', '/guide/how-to-run-guandan'],
      ['瑞士制编排规则', '/guide/swiss-system'],
      ['资讯', '/news'],
    ],
  },
]

const COMMON_RELATED: [string, string][] = [
  ['全部办赛指南', '/guide'],
  ['规则库', '/rules'],
  ['灵缇百科', '/baike'],
]

export const SPORTS: Sport[] = [
  {
    slug: 'guandan',
    name: '掼蛋',
    title: '掼蛋比赛编排计分系统 · 瑞士制/团团转/复式赛一键编排 | 灵缇赛事系统',
    h1: '掼蛋比赛编排与计分系统',
    description:
      '灵缇赛事系统是专业的掼蛋比赛编排软件:支持双人瑞士制、团团转、四人团体、复式赛、联赛等多种掼蛋赛制,在线报名收费、扫码签到、一键编排桌次、扫码录分、自动排名、证书与投屏,网页和小程序都能用。',
    keywords: '掼蛋比赛编排,掼蛋编排软件,掼蛋记分软件,掼蛋比赛系统,掼蛋赛事系统,掼蛋瑞士制,团团转,复式掼蛋,掼蛋比赛小程序',
    tldr: `灵缇赛事系统是一款面向掼蛋赛事的编排与计分平台,自 ${STATS.since} 上线以来已承办 ${STATS.matches} 场比赛、服务 ${STATS.users} 用户。它能完成掼蛋比赛从报名、签到、编排、计时、录分到排名、证书的全流程,支持瑞士制、团团转、复式赛、联赛等主流赛制。`,
    formats: [
      { name: '双人赛(组队赛)', desc: '最常用的掼蛋双人赛,瑞士制动态配对,免费使用' },
      { name: '团团转', desc: '个人计分、每副换搭档,支持 6 分制与 3/2/1/0 场分' },
      { name: '单人赛 / 全国个人公开赛', desc: '个人报名、系统组队编排' },
      { name: '团队瑞士移位赛', desc: '以团体为单位瑞士配对,团体先对阵再逐桌交锋' },
      { name: '团队分组循环赛', desc: '分组循环、裁判长登分、组内榜 + 总榜' },
      { name: '四人队式开闭室复式赛', desc: '最经典的团体复式,开室闭室座位对换' },
      { name: '瑞士偏差分复式赛 / 偏差分团团转复式赛', desc: '动态配对 + 同牌比较,消除牌运' },
      { name: '米切尔复式赛 / 单人复式循环赛', desc: '复式移位自动排表、Matchpoint 自动计分' },
      { name: '循环赛 / 双败制 / 超分赛 / 联赛', desc: '覆盖俱乐部月赛、多站联赛等场景' },
    ],
    blocks: [
      {
        h2: '为什么选灵缇办掼蛋比赛',
        ul: [
          '一键编排:瑞士制配对三原则(成绩相近、不重复对阵、同俱乐部回避)、种子、轮空全部自动处理。',
          '扫码录分:选手手机扫码录入比分,裁判长审核,改分全程留痕可查。',
          '实时排名:排名依据可拖动调整,排名页、投屏页实时同步。',
          '报名收费:在线报名、微信支付、自动对账;严格签到防冒名。',
          '复式发牌机联动:与灵缇复式发牌机深度集成,办复式掼蛋只需一台机器。',
          '规则库:收录《竞技掼蛋竞赛规则》《江苏省掼蛋竞赛规则》《世界掼牌(掼蛋)竞赛规则》等全文。',
        ],
      },
    ],
    faq: [
      { q: '掼蛋比赛用什么软件编排最方便?', a: '灵缇赛事系统(hy.lingti.com)支持网页和微信小程序,创建比赛后一键生成每轮桌次,选手扫码录分,系统自动排名,适合几桌到上百桌的掼蛋比赛。' },
      { q: '掼蛋双人赛免费吗?', a: '双人赛(组队赛)等常用赛制可以免费使用;部分高级赛制与功能需要 VIP 或灵缇点。' },
      { q: '可以办线上掼蛋比赛吗?', a: '可以,平台提供线上团团转和线上偏差分团团转复式赛。' },
    ],
    related: [
      ['掼蛋规则入门', '/guide/guandan-rules'],
      ['如何组织一场掼蛋比赛', '/guide/how-to-run-guandan'],
      ['瑞士制编排规则', '/guide/swiss-system'],
      ['团团转规则', '/guide/tuantuanzhuan'],
      ['掼蛋计分与排名', '/guide/guandan-scoring'],
      ['掼蛋竞赛规则全文', '/rules/g/guandan'],
      ...COMMON_RELATED,
    ],
  },
  {
    slug: 'bridge',
    name: '桥牌',
    title: '桥牌复式赛编排计分系统 · 米切尔/豪厄尔移位 + 复式发牌机 | 灵缇赛事系统',
    h1: '桥牌复式赛编排与计分',
    description:
      '灵缇赛事系统支持桥牌比赛与中华侨牌:米切尔、豪厄尔复式移位自动排表,Matchpoint 自动计分;灵缇复式发牌机支持桥牌模式,按牌谱自动发牌,杜绝插错方位。',
    keywords: '桥牌比赛编排,桥牌复式赛,桥牌计分软件,米切尔移位,豪厄尔移位,Matchpoint,桥牌发牌机,中华侨牌',
    tldr: '灵缇赛事系统可以编排桥牌复式赛:按桌数自动生成米切尔(南北/东西双冠军)或豪厄尔(单冠军)移位表,扫码录分后自动计算 Matchpoint 百分比排名;配合支持桥牌模式的灵缇复式发牌机,备牌、发牌、计分全流程自动化。',
    formats: [
      { name: '桥牌', desc: '桥牌比赛编排与计分' },
      { name: '米切尔复式赛', desc: '南北固定、东西流动,完全/不完全米切尔自动处理' },
      { name: '中华侨牌(6 人)', desc: '6 人中华侨牌赛制' },
    ],
    blocks: [
      {
        h2: '桥牌复式赛的关键',
        ul: [
          '移位:单数桌完全米切尔 n 轮、双数桌不完全米切尔 n−1 轮并跳桌;桌少要唯一冠军用豪厄尔。',
          '计分:同一副牌各桌横比,胜 2 / 平 1 / 负 0,每副满分 =(桌数 − 1)× 2。',
          '备牌:发牌机按牌谱发牌,每副可复现,赛后可复盘。',
          '规则:规则库收录《世界桥牌竞赛规则(Laws of Duplicate Bridge 2017)》。',
        ],
      },
    ],
    faq: [
      { q: '桥牌比赛双数桌怎么移位?', a: '双数桌使用不完全米切尔,打 n−1 轮,中途东西对多跳一桌避免撞车;系统自动生成移位表。' },
      { q: '灵缇复式发牌机支持桥牌吗?', a: '支持,复式发牌机有桥牌模式,同时覆盖桥牌与掼蛋,也支持双升、斗地主等牌类。' },
    ],
    related: [
      ['米切尔与豪厄尔对比', '/guide/mitchell-howell'],
      ['复式赛是什么', '/guide/duplicate'],
      ['世界桥牌竞赛规则', '/rules/bridge-wbf-2017'],
      ['复式发牌机', '/fushiji'],
      ...COMMON_RELATED,
    ],
  },
  {
    slug: 'pickleball',
    name: '匹克球',
    title: '匹克球比赛编排计分系统 · 小组循环淘汰赛/双打/单打一键编排 | 灵缇赛事系统',
    h1: '匹克球比赛编排与计分系统',
    description:
      '灵缇赛事系统支持匹克球双打、单打、循环赛、淘汰赛、小组循环 + 淘汰赛:蛇形分组、一键编排、自动晋级、决出冠亚季;目标分 11/15/21、三局两胜、rally 或 side-out 计分均可配置。',
    keywords: '匹克球比赛,匹克球赛事,匹克球编排,匹克球计分,pickleball,匹克球小组赛,匹克球淘汰赛,匹克球双打,匹克球比赛软件',
    tldr: '在灵缇赛事系统创建「球拍类小组淘汰赛」,设置小组数和每组晋级数后一键生成全部小组对局,小组赛结束自动生成 1/4 决赛、半决赛、决赛与季军赛;支持双打/单打、11/15/21 分制、单局/三局两胜/五局三胜和 rally/side-out 计分。',
    formats: [
      { name: '球拍类小组淘汰赛', desc: '蛇形分组循环 + 单败淘汰,8–32 人锦标赛首选' },
      { name: '球拍类双打赛', desc: '2v2 双打,瑞士制轮次编排' },
      { name: '球拍类单打赛', desc: '1v1 单打,按胜场/总分排名' },
      { name: '球拍类循环赛', desc: '所有队伍两两对阵一次' },
      { name: '球拍类淘汰赛', desc: '单败淘汰直至冠军' },
    ],
    blocks: [
      {
        h2: '匹克球计分可配置项',
        table: {
          head: ['配置', '可选值'],
          rows: [
            ['每局目标分', '11 / 15 / 21'],
            ['领先分', '1 / 2(可设封顶分)'],
            ['局数', '单局 / 三局两胜 / 五局三胜'],
            ['计分制', 'rally(每球得分)/ side-out(发球得分)'],
            ['模式', '双打 / 单打'],
          ],
        },
      },
    ],
    faq: [
      { q: '匹克球比赛 16 人怎么编排?', a: '双打 16 队常用 4 组 × 4 队,每组 6 场循环,每组前 2 进 8 强淘汰赛,再打半决赛、决赛和季军赛。' },
      { q: '匹克球是 11 分制吗?', a: '常规为 11 分、领先 2 分获胜;灵缇也支持 15、21 分及封顶分设置。' },
    ],
    related: [
      ['小组循环 + 淘汰赛编排', '/guide/group-knockout'],
      ['匹克球国际竞赛规则', '/rules/pickleball-world-2024'],
      ['网球/羽毛球/乒乓球', '/sports/racquet'],
      ...COMMON_RELATED,
    ],
  },
  {
    slug: 'racquet',
    name: '网球 / 羽毛球 / 乒乓球',
    title: '网球/羽毛球/乒乓球比赛编排系统 · 小组赛+淘汰赛自动晋级 | 灵缇赛事系统',
    h1: '网球、羽毛球、乒乓球比赛编排',
    description:
      '灵缇赛事系统的球拍类赛制适用于网球、羽毛球、乒乓球等项目:单打/双打、循环赛、淘汰赛、小组循环 + 淘汰赛,一键编排、场地分配、自动晋级、冠亚季判定。',
    keywords: '网球比赛编排,羽毛球比赛编排,乒乓球比赛编排,羽毛球比赛软件,乒乓球比赛软件,网球比赛计分,球类赛事系统,单打双打',
    tldr: '网球、羽毛球、乒乓球都可以用灵缇的「球拍类」赛制办:选择小组淘汰赛、循环赛、淘汰赛或双打/单打赛,录入选手和场地后一键编排,系统按可配置的计分规则自动算胜负、排名和晋级。',
    formats: [
      { name: '球拍类小组淘汰赛', desc: '小组循环 + 单败淘汰,自动晋级与季军赛' },
      { name: '球拍类循环赛', desc: '两两对阵,适合小规模锦标赛' },
      { name: '球拍类淘汰赛', desc: '单败淘汰' },
      { name: '球拍类双打 / 单打赛', desc: '轮次编排,按胜场/总分排名' },
    ],
    blocks: [
      {
        h2: '各项目建议配置',
        table: {
          head: ['项目', '常用目标分', '常用局数'],
          rows: [
            ['羽毛球', '21 分(领先 2 分,30 分封顶)', '三局两胜'],
            ['乒乓球', '11 分(领先 2 分)', '三局两胜或五局三胜'],
            ['网球(业余抢分赛)', '11 / 15 / 21 分抢分制', '单局或三局两胜'],
          ],
        },
        p: ['规则库收录《国际网球规则(ITF)》《世界羽毛球竞赛规则(BWF)》全文,可直接查阅。'],
      },
    ],
    faq: [
      { q: '羽毛球比赛可以用灵缇编排吗?', a: '可以,选择球拍类赛制,把目标分设为 21、领先 2 分、三局两胜即可。' },
      { q: '可以按场地排赛吗?', a: '可以,先创建场地,编排时会把对局分配到各个场地。' },
    ],
    related: [
      ['小组循环 + 淘汰赛编排', '/guide/group-knockout'],
      ['循环赛编排', '/guide/round-robin'],
      ['国际网球规则', '/rules/tennis-itf'],
      ['世界羽毛球竞赛规则', '/rules/badminton-bwf'],
      ['匹克球赛事', '/sports/pickleball'],
      ...COMMON_RELATED,
    ],
  },
  {
    slug: 'shengji',
    name: '升级 / 双升 / 斗地主',
    title: '升级/双升/斗地主比赛编排计分系统 · 复式赛可用 | 灵缇赛事系统',
    h1: '升级、双升、斗地主比赛编排',
    description:
      '灵缇赛事系统支持双升、四人手抓牌升级赛、三人赛(斗地主)等扑克比赛的编排与计分,也可以办复式双升、复式斗地主,配合复式发牌机消除牌运。',
    keywords: '双升比赛,升级比赛,斗地主比赛,斗地主比赛编排,双扣比赛,扑克比赛软件,复式斗地主,复式双升',
    tldr: '灵缇赛事系统提供双升、四人手抓牌升级赛和三人赛等赛制,扫码录分、自动排名;复式发牌机同样支持双升、斗地主,可以把这些项目办成同牌比较的复式赛。',
    formats: [
      { name: '双升', desc: '双升比赛编排与计分' },
      { name: '四人手抓牌升级赛', desc: '四人队手抓牌升级' },
      { name: '三人赛', desc: '适用于斗地主等三人项目' },
    ],
    blocks: [
      {
        h2: '让扑克比赛更公平',
        p: [
          '升级、斗地主的胜负受牌运影响很大。复式赛让多桌打同一副牌再比较,能把运气挤出牌桌。规则库收录了《斗地主竞赛规则(三人经典·综合整理版)》。',
        ],
      },
    ],
    faq: [{ q: '斗地主可以办复式赛吗?', a: '可以。复式的核心是多桌打同一副牌再比较,灵缇复式发牌机支持斗地主,可按牌谱为多桌发出相同的牌。' }],
    related: [
      ['复式赛是什么', '/guide/duplicate'],
      ['斗地主竞赛规则', '/rules/doudizhu-folk'],
      ['复式发牌机', '/fushiji'],
      ...COMMON_RELATED,
    ],
  },
]

export const findGuide = (slug: string) => GUIDES.find((g) => g.slug === slug)
export const findSport = (slug: string) => SPORTS.find((s) => s.slug === slug)

// 给 llms.txt / llms-full.txt 用的 Markdown 版本
const blocksToMd = (blocks: Block[]) =>
  blocks
    .map((b) => {
      const out = [`### ${b.h2}`]
      b.p?.forEach((t) => out.push(t))
      b.ol?.forEach((t, i) => out.push(`${i + 1}. ${t}`))
      b.ul?.forEach((t) => out.push(`- ${t}`))
      if (b.table) {
        out.push(`| ${b.table.head.join(' | ')} |`, `| ${b.table.head.map(() => '---').join(' | ')} |`)
        b.table.rows.forEach((r) => out.push(`| ${r.join(' | ')} |`))
      }
      return out.join('\n')
    })
    .join('\n\n')

const faqToMd = (faq: QA[]) => faq.map(({ q, a }) => `**问:${q}**\n答:${a}`).join('\n\n')

export const guideToMarkdown = (g: Guide, base: string) =>
  [
    `## ${g.h1}`,
    `来源:${base}/guide/${g.slug} · 更新于 ${g.updated}`,
    `> ${g.tldr}`,
    blocksToMd(g.blocks),
    g.howto ? `### ${g.howto.name}\n${g.howto.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}` : '',
    g.faq.length ? `### 常见问题\n${faqToMd(g.faq)}` : '',
  ]
    .filter(Boolean)
    .join('\n\n')

export const sportToMarkdown = (s: Sport, base: string) =>
  [
    `## ${s.h1}`,
    `来源:${base}/sports/${s.slug}`,
    `> ${s.tldr}`,
    `### 支持的${s.name}赛制\n${s.formats.map((f) => `- **${f.name}**:${f.desc}`).join('\n')}`,
    blocksToMd(s.blocks),
    s.faq.length ? `### 常见问题\n${faqToMd(s.faq)}` : '',
  ]
    .filter(Boolean)
    .join('\n\n')

// 百科词条 → 深入讲解的指南/页面(词条页底部「深入阅读」内链)
const guideLink = (slug: string): [string, string] => [GUIDES.find((g) => g.slug === slug)?.h1 ?? slug, `/guide/${slug}`]
const BAIKE_GROUPS: [string[], [string, string]][] = [
  [['swiss', 'buchholz', 'bye', 'seed', 'first-round-four', 'pairing-three-rules', 'points-race'], guideLink('swiss-system')],
  [['mitchell', 'howell', 'complete-mitchell', 'incomplete-mitchell', 'skip', 'relay', 'ns-ew', 'winners-two-one', 'collision'], guideLink('mitchell-howell')],
  [['duplicate', 'board', 'matchpoint', 'fushisaijianjie'], guideLink('duplicate')],
  [['rank-order', 'opponent-score', 'rank-up-score', 'match-score', 'victory-count', 'victory-score'], guideLink('guandan-scoring')],
  [['ttzgz'], guideLink('tuantuanzhuan')],
  [['round-robin'], guideLink('round-robin')],
  [['elimination', 'single-double-loss'], guideLink('group-knockout')],
  [['fushiji', 'fapaiji'], ['灵缇三代复式发牌机', '/fushiji']],
]
export const baikeDeepLinks = (shortName: string): [string, string][] => BAIKE_GROUPS.filter(([keys]) => keys.includes(shortName)).map(([, link]) => link)
